import { useSyncExternalStore } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from './api';
import { useGuilds, useMe } from './hooks';
import { pushToast } from './toasts';
import { useLiveSocket } from './ws';

/**
 * Backup runs (create-backup, restore-backup, guild-clone) as the web-api
 * tracks them (plugins/botRuns.ts): `GET /api/runs` once, then live
 * snapshots on the `runs` channel of /ws/live. Running runs survive reloads;
 * finished ones stay 30 min so their card can still show "Fertig ✓".
 */
export type RunAction = 'create-backup' | 'restore-backup' | 'guild-clone';

export interface BotRun {
  id: string;
  action: RunAction;
  /** Server the run works on (clone: the target). */
  guildId: string;
  sourceGuildId: string | null;
  /** restore: the backup loaded; create: the new backup once saved. */
  backupId: string | null;
  areas: string[];
  requestedBy: string;
  requestedByName: string;
  status: 'running' | 'ok' | 'failed';
  phase: string | null;
  phaseDone: number;
  phaseTotal: number;
  done: number;
  total: number;
  error: string | null;
  report: unknown;
  startedAt: string;
  updatedAt: string;
  finishedAt: string | null;
  seq: number;
}

/** Restore / clone change the server — one at a time per server. */
export const LOCKING: RunAction[] = ['restore-backup', 'guild-clone'];

const RUNS_KEY = ['runs'];

export function useRuns() {
  return useQuery({
    queryKey: RUNS_KEY,
    queryFn: () => api<{ runs: BotRun[] }>('/api/runs'),
    select: (d) => d.runs,
  });
}

/** Puts a run snapshot into the cache (newer `seq` wins). */
export function upsertRun(qc: QueryClient, run: BotRun) {
  qc.setQueryData<{ runs: BotRun[] }>(RUNS_KEY, (old) => {
    const runs = old?.runs ?? [];
    const current = runs.find((r) => r.id === run.id);
    if (current && current.seq >= run.seq) return old;
    return {
      runs: [run, ...runs.filter((r) => r.id !== run.id)].sort((a, b) => b.startedAt.localeCompare(a.startedAt)),
    };
  });
}

/** The running restore / clone on one of these servers, if any. */
export function lockingRun(runs: BotRun[] | undefined, guildIds: string[]): BotRun | null {
  return runs?.find((r) => r.status === 'running' && LOCKING.includes(r.action) && guildIds.includes(r.guildId)) ?? null;
}

// Finished runs the viewer closed ("×") — per tab, survives reloads.
const DISMISSED_KEY = 'dismissed-runs';
let dismissed: string[] = (() => {
  try {
    return JSON.parse(sessionStorage.getItem(DISMISSED_KEY) ?? '[]') as string[];
  } catch {
    return [];
  }
})();
const dismissListeners = new Set<() => void>();

export function dismissRun(id: string) {
  dismissed = [...dismissed.slice(-50), id];
  try {
    sessionStorage.setItem(DISMISSED_KEY, JSON.stringify(dismissed));
  } catch {
    // storage blocked — dismissal lasts until reload
  }
  dismissListeners.forEach((listener) => listener());
}

/** Newest run matching `match` that is running, or finished and not dismissed. */
export function useShownRun(match: (run: BotRun) => boolean): BotRun | null {
  const runs = useRuns();
  const hidden = useSyncExternalStore(
    (onChange) => {
      dismissListeners.add(onChange);
      return () => dismissListeners.delete(onChange);
    },
    () => dismissed
  );
  return runs.data?.find((r) => match(r) && (r.status === 'running' || !hidden.includes(r.id))) ?? null;
}

export const RUN_LABEL: Record<RunAction, { running: string; ok: string; failed: string }> = {
  'create-backup': { running: 'Backup wird erstellt', ok: 'Backup erstellt ✓', failed: 'Backup fehlgeschlagen' },
  'restore-backup': {
    running: 'Wiederherstellung läuft',
    ok: 'Wiederherstellung fertig ✓',
    failed: 'Wiederherstellung fehlgeschlagen',
  },
  'guild-clone': { running: 'Klonen läuft', ok: 'Klonen fertig ✓', failed: 'Klonen fehlgeschlagen' },
};

/** Where the run's card is: the Backups page, or the clone card in the Admin-Panel. */
export function runLink(run: BotRun, isAdmin: boolean): string {
  if (run.action === 'guild-clone' && isAdmin) return '/admin#clone';
  const anchor =
    run.action === 'create-backup' ? 'new-backup' : run.action === 'restore-backup' ? `backup-${run.backupId}` : 'running';
  return `/server/${run.guildId}/backups#${anchor}`;
}

export const runPercent = (run: BotRun): number | null =>
  run.total > 0 ? Math.min(100, Math.round((run.done / run.total) * 100)) : null;

export interface BackupReport {
  areas?: string[];
  roles?: number;
  channels?: number;
  emojis?: number;
  emojisSkipped?: string[];
  reposted?: number;
  unmapped?: number;
  warnings?: string[];
  safetyBackupId?: string;
  dataRestored?: boolean;
  accessRestored?: boolean;
}

/** "39 Rollen · 39 Kanäle · 5 Panels/Nachrichten neu gepostet …" */
export function reportParts(report: BackupReport): string[] {
  return [
    report.roles ? `${report.roles} Rollen` : null,
    report.channels ? `${report.channels} Kanäle` : null,
    report.emojis ? `${report.emojis} Emojis` : null,
    report.reposted ? `${report.reposted} Panels/Nachrichten neu gepostet` : null,
    report.dataRestored ? 'Bot-Daten übernommen' : null,
    report.accessRestored ? 'Zugriffsrechte übernommen' : null,
  ].filter((p): p is string => !!p);
}

/**
 * Keeps the runs cache live (mounted once in the App shell): merges `runs`
 * snapshots, refetches after a reconnect, refreshes backup lists when a run
 * ends and toasts the person who started it — on whatever page they are.
 */
export function useRunsSync() {
  const qc = useQueryClient();
  const me = useMe();
  const guilds = useGuilds(true);

  useLiveSocket(
    (msg) => {
      if (msg.channel !== 'runs') return;
      const run = msg.payload as BotRun;
      const before = qc.getQueryData<{ runs: BotRun[] }>(RUNS_KEY)?.runs.find((r) => r.id === run.id);
      if (before && before.seq >= run.seq) return;
      upsertRun(qc, run);
      if (run.status === 'running' || before?.status !== 'running') return;

      // Just finished: new backup (create) or safety backup (clone) in the list.
      qc.invalidateQueries({ queryKey: ['backups', run.guildId] });
      if (run.requestedBy !== me.data?.user.discordId) return;
      const server = guilds.data?.guilds.find((g) => g.id === run.guildId)?.name ?? 'Server';
      const summary = run.report ? reportParts(run.report as BackupReport).join(' · ') : '';
      pushToast({
        title: RUN_LABEL[run.action][run.status],
        body: run.status === 'ok' ? [server, summary].filter(Boolean).join(' — ') : `${server}: ${run.error ?? 'unbekannter Fehler'}`,
        tone: run.status === 'ok' ? 'success' : 'error',
        to: runLink(run, !!me.data?.isAdmin),
      });
    },
    () => qc.invalidateQueries({ queryKey: RUNS_KEY })
  );
}
